import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import API from "../../../api/axios";

const getExtraId = (extra) => extra?.shopMotorcycleExtraId ?? extra?.id;

export default function useAccessoriesEditor({
    show,
    values,
    availableExtras,
    currentAccessories,
    customerCurrencyCode,
}) {
    const [leftAccessories, setLeftAccessories] = useState([]);
    const [rawRightAccessories, setRawRightAccessories] = useState([]); // ✅ 不存 isChecked
    const [search, setSearch] = useState("");
    const [tempIdCounter, setTempIdCounter] = useState(0);
    const prevShowRef = useRef(false);

    /* =====================================================
       1) 打开 Drawer 时请求 preview（右侧原始 extras）
    ===================================================== */
    useEffect(() => {
        const wasOpen = prevShowRef.current;
        prevShowRef.current = show;
        if (!show || wasOpen) return;

        const controller = new AbortController();

        (async () => {
            try {
                const shopId = values?.shopSnapshot?.pickup?.shop?.id;
                const shopMotorcycleId = values?.motorcycleSnapshot?.shopMotorcycle?.id;

                const startDate = values?.rentalOrder?.startDate;
                const endDate = values?.rentalOrder?.endDate;
                const startTime = values?.rentalOrder?.startTime;
                const endTime = values?.rentalOrder?.endTime;

                const supplierCurrencyCode =
                    values?.pricingSnapshot?.calculation?.supplierCurrencyCode;
                const customerCurrencyCodeFromValues =
                    values?.pricingSnapshot?.calculation?.customerCurrencyCode;

                const countryISOCode =
                    values?.shopSnapshot?.pickup?.location?.country?.ISOCode;

                if (!shopId || !shopMotorcycleId || !startDate || !endDate || !startTime || !endTime) {
                    setRawRightAccessories([]);
                    return;
                }

                const payload = {
                    shopId,
                    shopMotorcycleId,
                    startDate,
                    endDate,
                    startTime,
                    endTime,
                    supplierCurrencyCode,
                    customerCurrencyCode: customerCurrencyCode || customerCurrencyCodeFromValues,
                    countryISOCode,
                };

                const { data } = await API("/booking/extras/preview", {
                    method: "POST",
                    data: payload,
                    signal: controller.signal,
                });

                const extras = (data?.extras || []).map((extra) => ({
                    ...extra,
                    id: getExtraId(extra),
                }));

                setRawRightAccessories(extras);
            } catch (err) {
                if (err?.name === "AbortError") return;
                const fallback = (availableExtras || []).map((e) => ({ ...e, id: getExtraId(e) }));
                setRawRightAccessories(fallback);
            }
        })();

        return () => controller.abort();
    }, [show]); // intentionally only show

    /* =====================================================
       2) 初始化左侧（基于 currentAccessories + availableExtras）
    ===================================================== */
    useEffect(() => {
        if (!show) return;

        let nextTemp = 0;

        const processedLeft = (currentAccessories || []).map((acc) => {
            const matched = (availableExtras || []).find(
                (a) => a.id === acc.shopMotorcycleExtraId || a.shopMotorcycleExtraId === acc.shopMotorcycleExtraId
            );

            let tempId = null;
            let status = "normal";
            if (acc.shopMotorcycleExtraId === null) {
                tempId = `personalized-${nextTemp++}`;
                status = "personalized";
            }

            return {
                id: acc.id ?? null,
                shopMotorcycleExtraId: acc.shopMotorcycleExtraId, // personalized = null
                tempId,
                status,
                extraName: acc.name || matched?.ShopExtra?.Extra?.name || "Personalized Accessory",
                priceCents: matched?.totalWithFee ?? acc.priceCents ?? 0,
                qty: acc.quantity ?? 1,
                info: acc.description || matched?.description || "",
                size: acc.meta?.size || "",
                // ✅ 如果有可用性字段，后续可以接入：
                // isAvailable: matched?.isAvailable ?? true,
            };
        });

        setTempIdCounter(nextTemp);
        setLeftAccessories(processedLeft);
    }, [show, currentAccessories, availableExtras]);

    /* =====================================================
       3) 右侧过滤 + isChecked 派生（由 left 推导）
    ===================================================== */
    const checkedIdSet = useMemo(() => {
        const s = new Set();
        for (const acc of leftAccessories) {
            if (acc.shopMotorcycleExtraId) s.add(acc.shopMotorcycleExtraId);
        }
        return s;
    }, [leftAccessories]);

    const filteredRightAccessories = useMemo(() => {
        const q = search.trim().toLowerCase();

        const list = rawRightAccessories.map((extra) => {
            const id = getExtraId(extra);
            const isChecked = checkedIdSet.has(id);
            return { ...extra, id, isChecked };
        });

        // ✅ 先选中的排前面
        list.sort((a, b) => (a.isChecked === b.isChecked ? 0 : a.isChecked ? -1 : 1));

        if (!q) return list;

        return list.filter((extra) => {
            const name = extra?.ShopExtra?.Extra?.name || "";
            const desc = extra?.description || extra?.ShopExtra?.description || "";
            return `${name} ${desc}`.toLowerCase().includes(q);
        });
    }, [rawRightAccessories, search, checkedIdSet]);

    /* =====================================================
       4) toggleRight：只改 left（right 自动派生）
    ===================================================== */
    const toggleRight = useCallback((availableId) => {
        setLeftAccessories((prevLeft) => {
            const exists = prevLeft.some((acc) => acc.shopMotorcycleExtraId === availableId);
            if (exists) {
                return prevLeft.filter((acc) => acc.shopMotorcycleExtraId !== availableId);
            }

            const target = rawRightAccessories.find((e) => getExtraId(e) === availableId);
            if (!target) return prevLeft;

            return [
                ...prevLeft,
                {
                    id: null,
                    shopMotorcycleExtraId: availableId,
                    tempId: null,
                    status: "normal",
                    extraName: target?.name || "Accessory",
                    priceCents: target.totalWithFee ?? target.total ?? 0,
                    qty: 1,
                    info: target.description || target?.ShopExtra?.description || "",
                    size: "",
                },
            ];
        });
    }, [rawRightAccessories]);

    /* =====================================================
       5) personalized + 字段更新
    ===================================================== */
    const addPersonalized = useCallback(() => {
        setLeftAccessories((prev) => {
            const tempId = `personalized-${tempIdCounter}`;
            return [
                ...prev,
                {
                    id: null,
                    shopMotorcycleExtraId: null,
                    tempId,
                    status: "personalized",
                    extraName: "",
                    priceCents: 0,
                    qty: 1,
                    info: "",
                    size: "",
                },
            ];
        });
        setTempIdCounter((n) => n + 1);
    }, [tempIdCounter]);

    const removePersonalized = useCallback((tempId) => {
        setLeftAccessories((prev) => prev.filter((a) => a.tempId !== tempId));
    }, []);

    const updateLeftField = useCallback((shopMotorcycleExtraId, tempId, field, value) => {
        setLeftAccessories((prev) =>
            prev.map((a) => {
                const hit =
                    (tempId && a.tempId === tempId) ||
                    (!tempId && shopMotorcycleExtraId != null && a.shopMotorcycleExtraId === shopMotorcycleExtraId);

                if (!hit) return a;
                return { ...a, [field]: value };
            })
        );
    }, []);

    return {
        leftAccessories,
        rightAccessories: filteredRightAccessories, // ✅ 统一命名：rightAccessories 就是过滤后的
        filteredRightAccessories,                  // ✅ 为了兼容你 Drawer 旧写法也给一个别名
        search,
        setSearch,
        toggleRight,
        addPersonalized,
        removePersonalized,
        updateLeftField,
        setLeftAccessories, // 保留：万一外部要强制覆盖
        setRightAccessories: setRawRightAccessories,
    };
}